import React, {ReactNode, useEffect, useMemo, useState} from "react";
import {ViewState} from "@common/models/enums/viev-state.enum";
import DefaultLoading from "@common/components/default-loading";
import DefaultError from "@common/components/default-error";
import DefaultEmpty from "@common/components/default-empty";
import {StateCaseProps} from "@common/models/interfaces/smart-state.interface";


interface ViewStatesProps<DataType> {
    loading: boolean;
    error: string | null;
    data: DataType | null | undefined;
    children: ReactNode;
}

const ViewStates = <DataType, >({loading, error, data, children}: ViewStatesProps<DataType>) => {
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        if (data !== null && data !== undefined || error) {
            setIsInitialLoad(false);
        }
    }, [data, error]);

    const isEmpty = useMemo(() => {
        if (data === null || data === undefined) return true;
        if (Array.isArray(data)) return data.length === 0;
        if (typeof data === "object") return Object.keys(data).length === 0;
        return false;
    }, [data]);

    const currentState: ViewState = useMemo(() => {
        if (loading || isInitialLoad) return ViewState.LOADING;
        if (error) return ViewState.ERROR;
        if (!isEmpty) return ViewState.FETCHED;
        if (!loading && !isInitialLoad && isEmpty) return ViewState.EMPTY;
        return ViewState.LOADING;
    }, [loading, error, isEmpty, isInitialLoad]);

    const childrenArray = React.Children.toArray(children) as React.ReactElement<StateCaseProps>[];

    const match = childrenArray.find(
        (child) =>
            React.isValidElement(child) &&
            "state" in child.props &&
            (Array.isArray(child.props.state)
                ? child.props.state.includes(currentState)
                : child.props.state === currentState)
    );

    return match || null;
};


const State = (props: StateCaseProps) => {
    const {children, state} = props;
    if (children) {
        return <>{children}</>;
    }

    if (state === ViewState.LOADING) {
        return <DefaultLoading/>;
    }

    if (state === ViewState.ERROR) {
        return <DefaultError error={props.error || "Coś poszło nie tak"} onRetry={props.onRetry}/>;
    }

    if (state === ViewState.EMPTY) {
        return (
            <DefaultEmpty/>
        );
    }

    if (state === ViewState.FETCHED) {
        return <div>Dane załadowane {props.showCount && '(z licznikiem)'}</div>;
    }

    return null;
};

export {ViewStates, State};
